// Mocked data for Transformerus clone
export const BRAND = {
  name: 'transformerus',
  tagline: 'Build Full-Stack Web & Mobile Apps in minutes',
  badge: 'Y Combinator S24',
};

export const NAV_LINKS = [
  { label: 'Features', href: '#features' },
  { label: 'Pricing', href: '/pricing' },
  { label: 'FAQs', href: '#faqs' },
  { label: 'Enterprise', href: '#enterprise' },
];

export const HERO_SLIDES = [
  {
    id: 1,
    title: 'Built for teams',
    subtitle: 'Build, test and deploy with your favourite people in real-time, from concept to launch.',
    accent: 'from-cyan-300 via-sky-200 to-indigo-200',
  },
  {
    id: 2,
    title: 'Ship in minutes',
    subtitle: 'Go from a single prompt to a fully deployed product. Backend, frontend, database — all wired up.',
    accent: 'from-emerald-200 via-teal-200 to-cyan-200',
  },
  {
    id: 3,
    title: 'AI agents that build',
    subtitle: 'Spin up specialized agents for design, code, and QA. They collaborate so you don\'t have to.',
    accent: 'from-fuchsia-200 via-rose-200 to-orange-200',
  },
  {
    id: 4,
    title: 'One-click deploy',
    subtitle: 'Push your project live with a custom domain, SSL, and global CDN. No DevOps required.',
    accent: 'from-amber-200 via-yellow-100 to-lime-200',
  },
];

export const FEATURE_LIST = [
  {
    id: 'web',
    icon: 'Monitor',
    title: 'Build websites and mobile apps',
    desc: 'Transform your ideas into fully functional websites and mobile apps with instant deployment, seamless data connections, and powerful scalability.',
  },
  {
    id: 'agents',
    icon: 'Bot',
    title: 'Build custom agents',
    desc: 'Create specialized AI agents that automate workflows, answer questions, and execute multi-step tasks across your stack.',
  },
  {
    id: 'integrations',
    icon: 'Workflow',
    title: 'Build powerful integrations',
    desc: 'Connect Stripe, Slack, Notion, Postgres, and 100+ services. Securely store keys and call APIs in a single click.',
  },
  {
    id: 'deploy',
    icon: 'Rocket',
    title: 'Deploy in one click',
    desc: 'Custom domain, automatic SSL, global CDN, and zero-config CI/CD. Your project is live the moment it\'s built.',
  },
];

export const PRICING_TIERS = [
  {
    id: 'free',
    name: 'Free',
    icon: 'Gift',
    tagline: 'Get started with essential features at no cost',
    monthly: 0,
    annual: 0,
    save: null,
    features: [
      '10 free monthly credits',
      'Unlock all core platform features',
      'Build elegant Web and Mobile experiences',
      'Instant access to the most advanced models',
      'One-click LLM integration',
    ],
    cta: 'Try Transformerus',
    highlight: false,
  },
  {
    id: 'standard',
    name: 'Standard',
    icon: 'Compass',
    tagline: 'Perfect for first-time builders',
    monthly: 20,
    annual: 17,
    save: 36,
    features: [
      'Build web & mobile apps',
      'Private project hosting',
      '50 credits per month',
      'Purchase extra credits as needed',
      'GitHub integration',
      'Fork tasks',
    ],
    everythingIn: 'Free',
    cta: 'Try Transformerus',
    highlight: true,
  },
  {
    id: 'pro',
    name: 'Pro',
    icon: 'Sparkles',
    tagline: 'Built for serious creators and brands',
    monthly: 200,
    annual: 167,
    save: 396,
    features: [
      'Premium code-gen with Claude Sonnet 4.5',
      'Higher-quality, longer-form generations',
      'Priority queue (no rate-limit waits)',
      'Create custom AI agents',
      '750 Monthly Credits',
      'Priority customer support',
    ],
    everythingIn: 'Standard',
    cta: 'Try Transformerus',
    highlight: false,
  },
];

export const FAQ_ITEMS = [
  { q: 'What is Transformerus?', a: 'Transformerus is an AI-native development platform. Describe what you want to build in plain English and our agents will produce a production-ready full-stack application — frontend, backend, database, and deploy.' },
  { q: 'Do I need to know how to code?', a: 'No. Transformerus is built for non-developers and developers alike. You can ship a complete product without writing a line of code, or dive into the editor and tweak anything you want.' },
  { q: 'What kind of apps can I build?', a: 'SaaS dashboards, marketplaces, AI assistants, mobile apps, internal tools, landing pages, and more. If you can describe it, you can ship it.' },
  { q: 'How does pricing work?', a: 'You start free with 10 monthly credits. Upgrade anytime to Standard or Pro for more credits, private hosting, and advanced models. Annual plans save you up to 16%.' },
  { q: 'Is my code private?', a: 'Yes. All projects on Standard and above are private by default. You can export the code or push it to your own GitHub at any time.' },
  { q: 'What models does Transformerus use?', a: 'We use frontier models from OpenAI, Anthropic, and Google — automatically selecting the best one for your task. Pro users get access to the largest context windows and ultra-thinking modes.' },
];

export const SHOWCASE_APPS = [
  {
    id: 1,
    title: 'TrekVibe',
    desc: 'A community for hikers and nature lovers.',
    tag: 'Community',
    gradient: 'from-emerald-200 to-cyan-200',
    prompt:
      'Build a community web app called TrekVibe for hikers and nature lovers. Users can sign up, post trail reports with photos and difficulty ratings, comment on each other\'s reports, and follow other hikers. Include a discovery feed sorted by location, and a personal profile page showing completed trails.',
  },
  {
    id: 2,
    title: 'BookHub',
    desc: 'Discover and review your next favorite book.',
    tag: 'Marketplace',
    gradient: 'from-amber-200 to-rose-200',
    prompt:
      'Build BookHub, a book discovery + review marketplace. Users can search books by title/author/genre, leave star ratings + written reviews, build personal reading lists (want-to-read, reading, finished), and follow other readers. Include AI-powered "what should I read next?" recommendations based on a user\'s history.',
  },
  {
    id: 3,
    title: 'PulseFit',
    desc: 'Personalized AI fitness coach in your pocket.',
    tag: 'Health',
    gradient: 'from-rose-200 to-fuchsia-200',
    prompt:
      'Build PulseFit, an AI fitness coach. Users enter their fitness goal, available equipment, and weekly time commitment; the app generates a custom 4-week workout plan with daily routines, demos, and progress tracking. Include calorie estimates and a weekly check-in that adapts the plan.',
  },
  {
    id: 4,
    title: 'LedgerCRM',
    desc: 'A modern CRM for indie founders.',
    tag: 'B2B SaaS',
    gradient: 'from-sky-200 to-indigo-200',
    prompt:
      'Build LedgerCRM, a lightweight CRM for indie founders. Features: contact list with company, role, last-contacted timestamp and tags; deal pipeline with drag-and-drop stages (Lead → Demo → Proposal → Won/Lost); quick notes and follow-up reminders; a weekly digest email of stale deals. Authentication required.',
  },
  {
    id: 5,
    title: 'NoteNest',
    desc: 'AI-powered note taking that thinks with you.',
    tag: 'Productivity',
    gradient: 'from-violet-200 to-pink-200',
    prompt:
      'Build NoteNest, an AI note-taking app. Users can create rich-text notes organized into notebooks, search across all notes instantly, and select any text to get an AI action menu (Summarize, Expand, Translate, Action items). Notes auto-save and sync across devices. Include a daily journal mode.',
  },
  {
    id: 6,
    title: 'GreenCart',
    desc: 'Eco-friendly grocery delivery, reimagined.',
    tag: 'E-commerce',
    gradient: 'from-lime-200 to-emerald-200',
    prompt:
      'Build GreenCart, an eco-friendly grocery delivery storefront. Catalog of products grouped by category, each product showing carbon-footprint score; cart with checkout via Stripe; user accounts with order history; a "subscribe & save" flow for recurring grocery boxes. Admin dashboard to manage inventory.',
  },
  {
    id: 7,
    title: 'SonicTutor',
    desc: 'Language learning that adapts to your voice.',
    tag: 'Education',
    gradient: 'from-orange-200 to-amber-200',
    prompt:
      'Build SonicTutor, a voice-first language learning app. Users pick a target language and proficiency, then complete daily 5-minute lessons that mix vocabulary flashcards, listening comprehension, and speaking practice (using browser microphone). Track streaks, level up across CEFR tiers, and unlock new lesson packs.',
  },
  {
    id: 8,
    title: 'PixelDeck',
    desc: 'AI presentation builder for the design-conscious.',
    tag: 'Design',
    gradient: 'from-cyan-200 to-blue-200',
    prompt:
      'Build PixelDeck, an AI presentation builder. User describes their talk topic + audience; the app generates a full slide deck (~10 slides) with titles, bullet points, and stock-image suggestions. User can drag to reorder, edit any slide inline, and export as PDF/PPTX. Include a few design themes.',
  },
];

export const STATS = [
  { value: '120K+', label: 'Apps shipped' },
  { value: '4.9/5', label: 'Builder rating' },
  { value: '60s', label: 'Avg. first deploy' },
  { value: '24/7', label: 'AI agents online' },
];

export const TESTIMONIALS = [
  { name: 'Maya Chen', role: 'Founder, NoteNest', quote: 'I shipped my MVP in a single weekend. Transformerus replaced my entire dev team for the first 90 days.' },
  { name: 'Daniel Park', role: 'Indie Hacker', quote: 'It feels like pair-programming with a brilliant senior engineer who never sleeps.' },
  { name: 'Aisha Kapoor', role: 'PM at Lumen', quote: 'Our internal tools that used to take a sprint now take an afternoon. This is the future.' },
];

export const SAMPLE_PROMPTS = [
  {
    short: 'A Notion-style note-taking app with AI summaries',
    full: 'Build a Notion-style note-taking web app. Users can create notebooks, drag-and-drop nested pages, and edit each page with rich text. Selecting any block reveals an AI menu: Summarize, Expand, Translate. Auto-save, dark mode, real-time sync across devices.',
  },
  {
    short: 'A Stripe-powered subscription dashboard',
    full: 'Build a SaaS subscription dashboard. Pricing page with monthly + annual plans (Starter, Pro, Business), Stripe Checkout for payment, customer billing portal, admin view of all active subscriptions with churn metrics. Email receipts and dunning for failed payments.',
  },
  {
    short: 'An Airbnb clone for boutique hotels',
    full: 'Build an Airbnb-style booking site focused on boutique hotels. Properties list with photos, amenities, location map, nightly rate; search by city and dates; reservation flow with calendar availability; host dashboard to manage listings and bookings; reviews with star ratings.',
  },
  {
    short: 'A real-time multiplayer trivia game',
    full: 'Build a real-time multiplayer trivia game. Players create or join rooms via a 6-character code, see other players in the lobby, then race to answer multiple-choice questions live. Show a leaderboard at the end. Categories include movies, history, science, sports.',
  },
  {
    short: 'A personal CRM for founders',
    full: 'Build a personal CRM for indie founders. Contacts list with company, role, last-contacted date; tag system; deal pipeline with drag-and-drop stages; quick notes and follow-up reminders; weekly digest email of stale contacts.',
  },
];
